import { useEffect, useRef } from "react";

import { Avatar } from "@web-app/components";

import AcceptCallIcon from "./icons/AcceptCallIcon";
import DeclineCallIcon from "./icons/DeclineCallIcon";

const CALL_TIMEOUT_MS = 60000;
const BEEP_PAUSE_MS = 1500;

export interface IncomingCallData {
  conversationId: string;
  initiatorEmail: string;
  initiatorId: string;
  initiatorImage: string;
  initiatorName: string;
  initiatorPhone: string;
  isOrderScoped?: boolean;
  orderDisplayId?: string;
  orderExternalId?: string;
  orderId?: string;
  participantIds?: string[];
  roomName: string;
}

interface IncomingCallModalProps {
  callData: IncomingCallData;
  onAccept: () => void;
  onDecline: () => void;
}

export default function IncomingCallModal({
  callData,
  onAccept,
  onDecline,
}: IncomingCallModalProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio("/sounds/incoming-call.mp3");
    audioRef.current = audio;
    let pauseTimeout: ReturnType<typeof setTimeout> | null = null;

    const playWithPause = () => {
      audio.currentTime = 0;
      audio.play().catch((err) => {
        console.warn("Failed to play incoming call sound:", err);
      });
    };

    audio.addEventListener("ended", () => {
      pauseTimeout = setTimeout(playWithPause, BEEP_PAUSE_MS);
    });

    playWithPause();

    const timeout = setTimeout(() => {
      onDecline();
    }, CALL_TIMEOUT_MS);

    return () => {
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
      clearTimeout(timeout);
      if (pauseTimeout) clearTimeout(pauseTimeout);
    };
  }, [onDecline]);

  const handleAccept = () => {
    audioRef.current?.pause();
    onAccept();
  };

  const handleDecline = () => {
    audioRef.current?.pause();
    onDecline();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleDecline} />
      <div className="relative bg-white rounded-lg shadow-xl w-[360px] overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-gray-100">
          <Avatar
            image={callData.initiatorImage}
            name={callData.initiatorName || callData.initiatorEmail}
            size={10}
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">
              {callData.initiatorName || callData.initiatorEmail}
            </p>
            {callData.orderDisplayId && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span className="inline-block w-3 h-3">
                  <svg viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14.5a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13zm.75-9.75v4l3.25 1.9-.75 1.3L7.25 10V4.75h1.5z" />
                  </svg>
                </span>
                <span>{callData.orderDisplayId}</span>
              </div>
            )}
          </div>
          <button
            onClick={handleDecline}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="text-gray-500"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col items-center py-10 px-6">
          <div className="mb-4">
            <Avatar
              image={callData.initiatorImage}
              name={callData.initiatorName || callData.initiatorEmail}
              size={32}
            />
          </div>
          <p className="text-xl font-semibold text-gray-900 mb-1">
            {callData.initiatorName || callData.initiatorEmail}
          </p>
          {callData.initiatorPhone && (
            <p className="text-gray-500">{callData.initiatorPhone}</p>
          )}
        </div>

        <div className="flex justify-center gap-8 pb-8">
          <button
            onClick={handleDecline}
            className="transition-transform hover:scale-105 active:scale-95"
          >
            <DeclineCallIcon className="w-14 h-14" />
          </button>
          <button
            onClick={handleAccept}
            className="transition-transform hover:scale-105 active:scale-95"
          >
            <AcceptCallIcon className="w-14 h-14" />
          </button>
        </div>
      </div>
    </div>
  );
}
